<template>
  <material-card
    :title="`${ $t('Reconciliation') }`"
    color="dover-blue">
    <v-form
      ref="form"
      v-model="isValidationOk"
      lazy-validation>
      <v-container
        py-0
        fill-height
        fluid
        grid-list-xl>
        <v-layout
          wrap>
          <v-flex
            xs12
            md2>
            <MaglinkComponentTankList
              :loading="progressBar.loading"
              :filters="filters"
              :tanks="tanks" />
          </v-flex>
          <v-flex
            xs12
            md2>
            <MaglinkComponentDatePicker
              :component-key="1"
              :rules-is-date="rules.isDate"
              :initial-date="dateFromToday(-7)"
              :date-label="getDateLabel('From')"
              :date-format="systemSettings.dateFormat"
              :loading="progressBar.loading"
              :clear-date="true"
              @getDate="onGetDate"
              @checkDates="onCheckDates"/>
          </v-flex>
          <v-flex
            xs12
            md2>
            <MaglinkComponentDatePicker
              :component-key="2"
              :rules-is-date="rules.isDate"
              :initial-date="dateFromToday(0)"
              :date-label="getDateLabel('To')"
              :date-format="systemSettings.dateFormat"
              :loading="progressBar.loading"
              :clear-date="true"
              @getDate="onGetDate"
              @checkDates="onCheckDates"/>
          </v-flex>
          <v-flex
            xs12
            md4
            text-xs-left>
            <span
              class="caption grey--text font-weight-bold">
              {{ $t('Report type') }}
            </span>
            <br>
            <v-btn-toggle
              v-model="filters.reportType"
              dense
              group
              mandatory
              @change="onChangeReportType()">
              <v-btn
                v-for="(item) in reportTypes"
                :key="item.id"
                :value="item.id"
                text
                small
                color="dover-blue">
                {{ item.desc }}
              </v-btn>
            </v-btn-toggle>
          </v-flex>
          <v-flex
            xs6
            md1
            text-xs-left>
            <v-btn
              :disabled="!isValidationOk || progressBar.loading"
              small
              class="mx-0 font-weight-light"
              color="dover-blue"
              @click="applyFilter">
              {{ $t('Load') }}
            </v-btn>
          </v-flex>
          <v-flex
            xs6
            md1
            text-xs-right>
            <MaglinkComponentCsvExportButton
              :is-validation-ok="isValidationOk"
              :loading="progressBar.loading"
              @csvExport="csvExport" />
          </v-flex>
          <v-flex
            xs12
            md12
            lg12>
            <MaglinkComponentLoadingBar
              :loading="progressBar.loading"
              :indeterminate="false"
              :max-value="progressBar.maxValue"
              :value="progressBar.currentValue" />
            <span>{{ itemCountLabel }}</span>
            <hr>
          </v-flex>
          <v-flex
            v-show="filters.reportType === 0"
            xs12
            md12>
            <v-data-table
              :headers="headers"
              :items="dataSource"
              :no-data-text="`${ Maglink.NoDataMessage(false) }`"
              :no-results-text="`${ Maglink.NoDataMessage(true) }`"
              hide-actions>
              <template
                slot="headerCell"
                slot-scope="{ header }">
                <span
                  class="dover-light-blue-color"
                  v-text="header.text" />
              </template>
              <template
                slot="items"
                slot-scope="{ index, item }">
                <tr
                  :style="{backgroundColor: backColor(index, item) }"
                  class="text-xs-left">
                  <!--   <td class="text-xs-right">{{ item.id }}</td>  -->
                  <td class="text-xs-right">{{ item.lossRateVolume }}</td>
                  <td class="text-xs-right">{{ item.dateTime }}</td>
                  <td class="text-xs-right">{{ item.productVolume }}</td>
                  <td class="text-xs-right">{{ item.productLevel }}</td>
                  <td class="text-xs-right">{{ Maglink.TemperatureFormat(Maglink.TemperatureToDisplay(item.productTemperature, systemSettings.temperatureUnit)) }}</td>
                  <td class="text-xs-right">{{ item.saleVolume }}</td>
                  <td class="text-xs-right">{{ item.temperatureVarVolume }}</td>
                  <td class="text-xs-right">{{ item.evaporationVarVolume }}</td>
                  <td class="text-xs-right">{{ item.waterVarVolume }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
          <v-flex
            v-show="filters.reportType === 1"
            xs12
            md12>
            <v-data-table
              :headers="headers"
              :items="dataSource"
              :no-data-text="`${ Maglink.NoDataMessage(false) }`"
              :no-results-text="`${ Maglink.NoDataMessage(true) }`"
              hide-actions>
              <template
                slot="headerCell"
                slot-scope="{ header }">
                <span
                  class="dover-light-blue-color"
                  v-text="header.text" />
              </template>
              <template
                slot="items"
                slot-scope="{ index, item }">
                <tr
                  :style="{backgroundColor: backColor(index, item) }"
                  class="text-xs-left">
                  <!--   <td class="text-xs-right">{{ item.id }}</td>  -->
                  <td class="text-xs-right">{{ item.initialDate }}</td>
                  <td class="text-xs-right">{{ item.finalDate }}</td>
                  <td class="text-xs-right">{{ item.accountingVarVolume }}</td>
                  <td class="text-xs-right">{{ item.accountingVarPerc }}</td>
                  <td class="text-xs-right">{{ item.openingStockVolume }}</td>
                  <td class="text-xs-right">{{ item.closingStockVolume }}</td>
                  <td class="text-xs-right">{{ item.deliveriesVolume }}</td>
                  <td class="text-xs-right">{{ item.deliveriesVarVolume }}</td>
                  <td class="text-xs-right">{{ item.deliveriesVarPerc }}</td>
                  <td class="text-xs-right">{{ item.tankDepletionVolume }}</td>
                  <td class="text-xs-right">{{ item.pumpSalesVolume }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
          <v-flex
            v-show="filters.reportType === 2"
            xs12
            md12>
            <v-data-table
              :headers="headers"
              :items="dataSource"
              :no-data-text="`${ Maglink.NoDataMessage(false) }`"
              :no-results-text="`${ Maglink.NoDataMessage(true) }`"
              hide-actions>
              <template
                slot="headerCell"
                slot-scope="{ header }">
                <span
                  class="dover-light-blue-color"
                  v-text="header.text" />
              </template>
              <template
                slot="items"
                slot-scope="{ index, item }">
                <tr
                  :style="{backgroundColor: backColor(index, item) }"
                  class="text-xs-left">
                  <td class="text-xs-right">{{ item[0] }}</td>
                  <td class="text-xs-right">{{ item[1] }}</td>
                  <td class="text-xs-left">{{ item[2] }}</td>
                  <td class="text-xs-right">{{ item[3] }}</td>
                  <td class="text-xs-right">{{ item[4] }}</td>
                  <td class="text-xs-left">{{ item[5] }}</td>
                  <td class="text-xs-right">{{ item[6] }}</td>
                </tr>
              </template>
            </v-data-table>
          </v-flex>
        </v-layout>
      </v-container>
    </v-form>
  </material-card>
</template>

<script>
export default {
  props: {
    tanks: {
      type: Array,
      required: true,
      default: () => []
    },
    systemSettings: {
      type: Object,
      required: true,
      default: () => {}
    },
    options: {
      type: Object,
      required: true,
      default: () => {}
    },
    siteInfo: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data: () => ({
    isValidationOk: false,
    progressBar: {
      loading: false,
      maxValue: 100,
      currentValue: 0
    },
    rules: {
      isDate: [ v => true ]
    },
    itemCountLabel: '',
    volUdm: '',
    levelUdm: '',
    tempUdm: '',
    headers: [],
    dataSource: [],
    reportTypes: [],
    filters: {
      all: false, // used for MaglinkComponentTankList
      number: 1, // used for MaglinkComponentTankList
      tankId: 1,
      fromDay: '',
      toDay: '',
      initialDate: '', // int,
      finalDate: '', // int,
      reportType: 0 // 0 -“hourly”, 1 - “daily”, 2 -“petroleum”
    }
  }),
  mounted () {
    this.reportTypes.push({ id: 0, desc: this.$t('Hourly') })
    this.reportTypes.push({ id: 1, desc: this.$t('Daily') })
    this.reportTypes.push({ id: 2, desc: this.$t('Petroleum') })

    var currentDate = new Date()
    this.filters.toDay = currentDate.toISOString().substr(0, 10)

    currentDate.setDate(currentDate.getDate() - 7)
    this.filters.fromDay = currentDate.toISOString().substr(0, 10)

    this.volUdm = this.options.volumeUnits[this.systemSettings.volumeUnit || 0].desc
    this.levelUdm = this.options.levelUnits[this.systemSettings.levelUnit || 0].desc
    this.tempUdm = this.options.temperatureUnits.find(c => c.code === this.systemSettings.temperatureUnit).desc

    this.fillHeaders()
  },
  methods: {
    backColor (index, item) {
      return ((index % 2 === 0) ? '#cfdbfb' : 'transparent')
    },
    fillHeaders () {
      if (this.filters.reportType === 0) { // hourly
        this.headers = [
          // { sortable: false, text: '#', align: 'right' },
          { sortable: false, text: this.$t('Loss rate') + ' (' + this.volUdm + '/' + this.$t('Hour').toLowerCase() + ')', align: 'right' },
          { sortable: false, text: this.$t('Date time'), align: 'center' },
          { sortable: false, text: this.$t('Volume') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Level') + ' (' + this.levelUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Temp') + ' (' + this.tempUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Sales') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Temp. var.') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Evap. var.') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Water var.') + ' (' + this.volUdm + ')', align: 'right' }
        ]
      } else if (this.filters.reportType === 1) { // daily
        this.headers = [
          // { sortable: false, text: '#', align: 'right' },
          { sortable: false, text: this.$t('Initial date'), align: 'right' },
          { sortable: false, text: this.$t('Final date'), align: 'right' },
          { sortable: false, text: this.$t('Accounting var.') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: '%', align: 'right' },
          { sortable: false, text: this.$t('Opening stock') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Closing stock') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Deliveries') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Deliv, var.') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: '%', align: 'right' },
          { sortable: false, text: this.$t('Tank Depletion') + ' (' + this.volUdm + ')', align: 'right' },
          { sortable: false, text: this.$t('Sales'), align: 'right' }
        ]
      } else if (this.filters.reportType === 2) { // petroleum
        this.headers = []
      }
    },
    onChangeReportType () {
      this.itemCountLabel = ''
      this.dataSource = []
      this.fillHeaders()
    },
    applyFilter () {
      if (!this.onCheckDates()) return

      this.getDataSource()
    },
    getDataSource () {
      this.progressBar.loading = true
      this.dataSource = []

      // keep sync from standard fields and specific fields
      this.filters.tankId = this.filters.number
      this.filters.all = false
      this.filters.initialDate = this.filters.fromDay// Number(new Date(this.filters.fromDay))
      this.filters.finalDate = this.filters.toDay// Number(new Date(this.filters.toDay))
      // console.log(this.filters)

      this.Maglink.GetReportReconciliation(this.filters)
        .then(response => {
          // console.log(response.data)

          let isPetroleum = (this.filters.reportType === 2)
          let reports

          if (isPetroleum) {
            reports = this.Maglink.ToDisplay(response.data, this.systemSettings.levelUnit, this.systemSettings.volumeUnit, this.systemSettings.temperatureUnits) // response.data

            // row 1
            let row = [
              this.$t('Accounting var.'), reports.accountingVarVolume, this.volUdm,
              this.$t('Operating var.'), reports.operatingVarVolume, this.volUdm
            ]
            this.dataSource.push(row)

            // row 2
            row = [
              '', this.Maglink.RoundFormat(reports.accountingVarPerc, 1), '%',
              '', this.Maglink.RoundFormat(reports.operatingVarPerc, 1), '%'
            ]
            this.dataSource.push(row)

            // row 3
            row = [
              this.$t('Deliveries'), reports.deliveriesVolume, this.volUdm,
              this.$t('Temp var.'), reports.tempVarVolume, this.volUdm
            ]
            this.dataSource.push(row)

            // row 4
            row = [
              this.$t('Delivery var.'), reports.deliveriesVarVolume, this.volUdm,
              this.$t('Evap var.'), reports.evapVarVolume, this.volUdm
            ]
            this.dataSource.push(row)

            // row 5
            row = [
              '', this.Maglink.RoundFormat(reports.deliveriesVarPerc, 1), '%',
              this.$t('Water var.'), reports.waterVarVolume, this.volUdm
            ]
            this.dataSource.push(row)

            // row 6
            row = [
              this.$t('Tank Depletion'), reports.tankDepletionVolume, this.volUdm,
              this.$t('Pump Calib'), reports.pumpCalibVolume, this.volUdm
            ]
            this.dataSource.push(row)

            // row 7
            row = [
              this.$t('Pump sales'), reports.pumpSalesVolume, this.volUdm,
              this.$t('Theft'), reports.theftVolume, this.volUdm
            ]
            this.dataSource.push(row)

            // row 8
            row = [
              this.$t('Test Vend'), reports.testVendVolume, this.volUdm,
              this.$t('Unaccounted var'), reports.unaccountedVarVolume, this.volUdm
            ]
            this.dataSource.push(row)

            // row 9
            row = [
              '', '', '',
              '', this.Maglink.RoundFormat(reports.unaccountedVarPerc, 1), '%'
            ]
            this.dataSource.push(row)
            this.progressBar.loading = false
            this.itemCountLabel = ''
          } else {
            reports = response.data.reports
            this.progressBar.maxValue = reports.length

            for (var i = 0; i < reports.length; i++) {
              let item = reports[i]
              this.progressBar.currentValue = i

              if (this.filters.reportType === 0) {
                if (typeof item.dateTime === 'string') {
                  item.dateTime = new Date(item.dateTime)
                }

                item.dateTime = this.Maglink.GetCustomDateTime(item.dateTime, this.systemSettings.dateFormat)
                item.productTemperature = this.Maglink.TemperatureFormat(this.Maglink.TemperatureToDisplay(item.productTemperature, this.systemSettings.temperatureFormat) * 1)
              } else {
                if (typeof item.initialDate === 'string') {
                  item.initialDate = new Date(item.initialDate)
                }
                if (typeof item.finalDate === 'string') {
                  item.finalDate = new Date(item.finalDate)
                }
                item.initialDate = this.Maglink.GetCustomDateTime(item.initialDate, this.systemSettings.dateFormat)
                item.finalDate = this.Maglink.GetCustomDateTime(item.finalDate, this.systemSettings.dateFormat)
                item.accountingVarPerc = this.Maglink.RoundFormat(item.accountingVarPerc * 1, 1)
                item.deliveriesVarPerc = this.Maglink.RoundFormat(item.deliveriesVarPerc * 1, 1)
              }

              this.dataSource.push(this.Maglink.ToDisplay(item, this.systemSettings.levelUnit, this.systemSettings.volumeUnit, this.systemSettings.temperatureUnits))
              this.itemCountLabel = this.$t('Items: {0}', [ this.dataSource.length ])
            }
            this.progressBar.loading = false
          }
        })
        .catch(error => {
          this.progressBar.loading = false
          this.snack('error', error.message)
        })
    },
    csvExport (sep) {
      let fileContent = ''

      if (this.siteInfo.name) {
        fileContent += this.siteInfo.name + '\n'
      }
      if (this.siteInfo.header1) {
        fileContent += this.siteInfo.header1 + '\n'
      }
      if (this.siteInfo.header2) {
        fileContent += this.siteInfo.header2 + '\n'
      }
      if (this.siteInfo.header3) {
        fileContent += this.siteInfo.header3 + '\n'
      }

      let title = this.$t('Tank {0}', [ this.filters.number ])
      let reportTypeDesc = this.$t('Reconciliation') + ' ' + this.reportTypes[this.filters.reportType].desc
      fileContent += reportTypeDesc + '\n' + title + sep
      fileContent += this.tanks[this.filters.number - 1].tankDescription + '\n'
      fileContent += this.$t('Date from') + sep
      fileContent += (this.filters.fromDay !== undefined ? this.filters.fromDay : '') + '\n'
      fileContent += this.$t('Date to') + sep
      fileContent += (this.filters.toDay !== undefined ? this.filters.toDay : '') + '\n'
      fileContent += '\n'

      if (this.filters.reportType === 0) {
        // headers
        fileContent += this.getHeadearsToExport(sep)

        for (let index = 0; index < this.dataSource.length; index++) {
          let t = this.dataSource[index]
          // fileContent += t.id
          fileContent += t.lossRateVolume + sep
          fileContent += t.dateTime + sep
          fileContent += t.productVolume + sep
          fileContent += t.productLevel + sep
          fileContent += this.Maglink.TemperatureFormat(this.Maglink.TemperatureToDisplay(t.productTemperature, this.systemSettings.temperatureUnit)) + sep
          fileContent += t.saleVolume + sep
          fileContent += t.temperatureVarVolume + sep
          fileContent += t.evaporationVarVolume + sep
          fileContent += t.waterVarVolume + sep + '\n'
        }
      } else if (this.filters.reportType === 1) {
        // headers
        fileContent += this.getHeadearsToExport(sep)

        for (let index = 0; index < this.dataSource.length; index++) {
          let t = this.dataSource[index]
          // fileContent += t.id
          fileContent += t.initialDate + sep
          fileContent += t.finalDate + sep
          fileContent += t.accountingVarVolume + sep
          fileContent += t.accountingVarPerc + sep
          fileContent += t.openingStockVolume + sep
          fileContent += t.closingStockVolume + sep
          fileContent += t.deliveriesVolume + sep
          fileContent += t.deliveriesVarVolume + sep
          fileContent += t.deliveriesVarPerc + sep
          fileContent += t.tankDepletionVolume + sep
          fileContent += t.pumpSalesVolume + sep + '\n'
        }
      } else if (this.filters.reportType === 2) {
        for (let rowIndex = 0; rowIndex < this.dataSource.length; rowIndex++) {
          let row = this.dataSource[rowIndex]

          for (let colIndex = 0; colIndex < row.length; colIndex++) {
            fileContent += row[colIndex] + sep
          }
          fileContent += '\n'
        }
      }

      let filename = this.$t('Reconciliation report') + '__' + reportTypeDesc + '__' + title
      filename += '__' + (this.filters.fromDay !== undefined ? this.filters.fromDay : '')
      filename += '__' + (this.filters.toDay !== undefined ? this.filters.toDay : '')
      filename += '.csv'

      const link = this.Maglink.GetDownloadLinkCSV(fileContent, filename)
      document.body.appendChild(link)
      link.click()
    },
    getHeadearsToExport (sep) {
      let fileContent = ''
      for (let index = 0; index < this.headers.length; index++) {
        fileContent += this.headers[index].text + sep
      }
      fileContent += '\n'

      return fileContent
    },
    dateFromToday (days) {
      var currentDate = new Date()
      currentDate.setDate(currentDate.getDate() + days)

      return currentDate
    },
    getDateLabel (value) {
      return this.$t(value) + ' (' + this.$t(this.systemSettings.dateFormat) + ')'
    },
    onCheckDates () {
      // no check if one date is missing
      if (this.filters.fromDay === undefined) return true
      if (this.filters.toDay === undefined) return true

      if (this.filters.fromDay > this.filters.toDay) {
        this.isValidationOk = false
        this.rules.isDate = [this.$t('Wrong date value')]
      } else {
        this.rules.isDate = [ v => true ]
        this.isValidationOk = true
        this.$refs.form.resetValidation()
      }

      return this.isValidationOk
    },
    onGetDate (componentKey, day) {
      if (componentKey === 1) {
        this.filters.fromDay = day
      } else {
        this.filters.toDay = day
      }

      this.onCheckDates()
    },
    snack (color, message) {
      message = this.$t('Reconciliation') + ': ' + message
      this.$store.dispatch('showSnackbar', { color, message, show: true })
    }
  }
}
</script>
